import { Slot, SlotsItem } from '@/types';
import { format, isBefore, isEqual } from 'date-fns';
import { getDaysWithNoSlotsAvailable, retrieveFormattedSlotHour } from '../slotCalendarFunctions';
import { SKU_370_ENABLED_LOCATIONS, WHOLE_BODY_ENHANCED_SCREENING_SKU_ID } from '@/constants';

export const disabledDates = ({
  match,
  tzClosestAvailableDate,
  slotsData,
  skuId,
  locationId,
}: {
  match: Date;
  tzClosestAvailableDate: Date;
  slotsData: SlotsItem[];
  skuId: number;
  locationId: string | undefined;
}) => {
  const slotsWithNoAvailability = getDaysWithNoSlotsAvailable(slotsData);
  const closestAvailableDate = new Date(tzClosestAvailableDate);
  closestAvailableDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const foundDateAvailableForEnhancedScreening = SKU_370_ENABLED_LOCATIONS.find(
    (l) => l.locationId === locationId,
  )?.fistAvailableBookingDate;

  // removes dates that are not in the slotsData
  // and are before today
  if (!slotsData.find((slot) => slot.date === format(match, 'yyyy-MM-dd')) || match < today) {
    return true;
  }

  if (
    locationId &&
    skuId === WHOLE_BODY_ENHANCED_SCREENING_SKU_ID &&
    foundDateAvailableForEnhancedScreening &&
    isBefore(match, foundDateAvailableForEnhancedScreening)
  ) {
    return true;
  }
  if (slotsWithNoAvailability.some((date) => isEqual(match, date)) || match < closestAvailableDate)
    return true;
  else return false;
};

export const groupSlotsByHour = (slots: Slot[], timezone: string) => {
  const groupedSlots: { [key: string]: Slot[] } = {};

  slots.forEach((slot) => {
    const date = retrieveFormattedSlotHour(slot, timezone);
    const [time, period] = date.split(' ');
    let [hours] = time.split(':');
    hours = `${hours}:00 ${period}`;
    if (!groupedSlots[hours]) groupedSlots[hours] = [];
    groupedSlots[hours].push(slot);
  });

  return groupedSlots;
};

export const createHoldEventPayload = (
  machineId: string | undefined,
  utcStart: number | undefined,
  utcEnd: number | undefined,
) => {
  return {
    machineId,
    utcStart,
    utcEnd,
  };
};

export const removeHoldEventPayload = (
  eventId: string | undefined,
  machineId: string | undefined,
) => ({
  eventId,
  machineId,
});

export const createSessionPayload = (
  eventId: string | undefined,
  locationId: string,
  skuId: number,
  slot: Slot,
  machineId: string,
  promoCode: string,
) => ({
  sessionData: {
    eventId,
    locationId,
    member: {
      dob: '',
      email: '',
      firstname: '',
      gender: '',
      lastname: '',
      medicalReferral: false,
      phoneNumber: '',
      preferredName: '',
    },
    skuId,
    timeSlot: {
      machineId,
      utcEnd: slot.utcEnd,
      utcStart: slot.utcStart,
    },
    promoCode: promoCode || '',
  },
});

export const sortSlotsByStartTime = (slots: Slot[]) => {
  return [...slots].sort((a, b) => a.utcStart - b.utcStart);
};
